import { yupResolver } from '@hookform/resolvers/yup';
import { captureException } from '@sentry/nextjs';
import { fromRpcSig } from 'ethereumjs-util';
import { ethers } from 'ethers';
import { Trans, useTranslation } from 'next-i18next';
import { useContext, VFC, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'react-use';
import * as yup from 'yup';

import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import { useCreateMintBid } from '@/apis/aniftyBackend/mintBids/createMintBid';
import CustomButton from '@/components/CustomButton';
import CustomInput from '@/components/CustomInput';
import FloatingModal from '@/components/FloatingModal';
import Notice from '@/components/Notice';
import SquareCreationThumbnail from '@/components/SquareCreationThumbnail';
import { Web3Context } from '@/contexts/Web3Provider/Web3Provider';
import useCurrentWethBalance from '@/hooks/useCurrentWethBalance';
import { queryClient } from '@/utils/react-query';
import createMintBidParams from '@/utils/signingParams/createMintBidParams';
import { signTypedDataParams } from '@/utils/signingParams/signTypedDataParams';
import { truncateString } from '@/utils/truncate';

import styles from './CreationAuction.module.scss';
import { getUniswapExchangeLink } from '@/utils/uniswap';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  creation: V2CreationData;
  minimumBidPrice: number;
};

const ConfirmBidModal: VFC<Props> = ({
  isOpen,
  onClose,
  creation,
  minimumBidPrice,
}) => {
  const { t, i18n } = useTranslation();

  const web3Context = useContext(Web3Context);

  const [isProcessing, setIsProcessing] = useToggle(false);
  const [error, setError] = useState<null | string>(null);

  const {
    mutateAsync: createMintBid,
    status: createMintBidStatus,
    reset,
  } = useCreateMintBid();

  const { currentWethBalance, metamaskConnected } = useCurrentWethBalance();

  const schema = useMemo(
    () =>
      yup.object().shape({
        price: yup
          .number()
          .typeError(t('validation.number'))
          .min(minimumBidPrice, t('validation.bidPriceMin'))
          .max(Number(currentWethBalance), t('validation.bidPriceMax'))
          .required(t('validation.required')),
      }),
    [t, minimumBidPrice, currentWethBalance]
  );

  type Data = yup.InferType<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: Data) => {
    setError(null);

    if (isProcessing || createMintBidStatus === 'loading') {
      return;
    }

    setIsProcessing(true);

    try {
      const askHash = creation.mintAsk.askHash;
      const signer = web3Context.currentAccountAddress;
      const price = ethers.utils.parseEther(data.price.toString()).toString();

      const msgParams = createMintBidParams(askHash, signer, price);

      const userSignature = await signTypedDataParams(
        web3Context.web3.metamaskProvider,
        web3Context.currentAccountAddress,
        msgParams
      );

      const { v, r, s } = fromRpcSig(userSignature);

      await createMintBid({
        askHash,
        signer,
        price,
        v,
        r: r.toString('hex'),
        s: s.toString('hex'),
      });

      queryClient.invalidateQueries(['mintAsk', creation.mintAsk.askHash]);
    } catch (err) {
      if (err.code === 4001) {
        // User denied signing
        return;
      }
      captureException(err);

      if (err.response.status >= 500) {
        setError(
          i18n.language === 'ja'
            ? 'サーバーとの通信に失敗しました。少々お待ちいただいてから再度お試しください。解決しない場合はANIFTYチームにお問い合わせください。'
            : 'Failed to communicate with the server. Please wait a moment and try again. If the problem persists, please contact our team.'
        );
      } else {
        setError(err.response.data);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const onRequestClose = () => {
    onClose();
    reset();
  };

  return (
    <FloatingModal isOpen={isOpen} onRequestClose={onRequestClose}>
      <div className={styles.confirmBidModalContainer}>
        <p className={styles.currentBalance}>
          {t('creationPage.confirmBidModal.owningWeth', {
            currentBalance: truncateString(
              metamaskConnected && currentWethBalance
                ? currentWethBalance
                : '0.0',
              5
            ),
          })}
        </p>

        <div className={styles.convertWeth}>
          <p className={styles.convertWethDescription}>
            {t('creationPage.confirmBidModal.convertWethDescription')}
          </p>
          <a
            href={getUniswapExchangeLink()}
            className={styles.uniswapLinkButton}
            target="_blank"
            rel="nofollow noreferrer noopener"
          >
            {t('creationPage.confirmBidModal.swapOnUniswap')}
          </a>
        </div>
        <hr className={styles.dottedDivider} />
        <SquareCreationThumbnail
          creation={creation}
          size={150}
          className={styles.creationThumbnail}
        />
        <p className={styles.confirmationText}>
          {t('creationPage.confirmBidModal.confirmation')}
        </p>
        <form className={styles.bidForm} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.inputWithAddon}>
            <CustomInput
              type="number"
              min={minimumBidPrice}
              step={0.01}
              placeholder={`${minimumBidPrice}+`}
              {...register('price')}
            />
            <span>WETH</span>
          </div>

          <CustomButton
            className={styles.bidConfirmationButton}
            type="submit"
            loading={createMintBidStatus === 'loading' || isProcessing}
          >
            {t('creationPage.confirmBidModal.bidButton')}
          </CustomButton>
        </form>
        {errors.price && (
          <div className={styles.error}>{errors.price.message}</div>
        )}
        {error && <div className={styles.error}>{error}</div>}
        {createMintBidStatus === 'success' && (
          <div className={styles.success}>
            {t('creationPage.confirmBidModal.success')}
          </div>
        )}

        <Notice iconColor="gray">
          {t('creationPage.confirmBidModal.lastMinuteNotice')}
        </Notice>

        <Notice iconColor="orange">
          <Trans
            i18nKey="creationPage.confirmBidModal.noteContent"
            components={{
              heading: <span className={styles.noticeTitle} />,
              strong: <strong />,
            }}
          />
        </Notice>
      </div>
    </FloatingModal>
  );
};

export default ConfirmBidModal;
